import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Megaphone, Copy, Download, Sparkles, Loader2, Languages, CalendarClock, Trash2, QrCode as QrCodeIcon } from "lucide-react";
import { SalesPitchPanel } from "@/components/SalesPitchPanel";
import { AiVideoAdsPanel } from "@/components/AiVideoAdsPanel";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "sw", label: "SW" },
  { code: "ha", label: "HA" },
];

const CreativeExtras = ({ c, idx, campaigns }) => {
  const [translated, setTranslated] = useState(null);
  const [busyLang, setBusyLang] = useState(null);
  const [date, setDate] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [qrBusy, setQrBusy] = useState(false);

  const downloadQrPoster = async () => {
    if (!qrCode) return toast.error("Pick a campaign link first");
    setQrBusy(true);
    try {
      const file = c.img.split("/").pop();
      const res = await api.get(`/campaigns/poster/${qrCode}.png?flier=${file}`, { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vismarks-${qrCode}-${file.replace(".jpg", "")}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR poster downloaded — print or post it");
    } catch {
      toast.error("Could not build the QR poster");
    } finally {
      setQrBusy(false);
    }
  };

  const translate = async (lang) => {
    setBusyLang(lang);
    try {
      const { data } = await api.post("/campaigns/translate-caption", { caption: c.caption, language: lang }, { timeout: 60000 });
      setTranslated({ lang, text: data.text });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Translation failed");
    } finally {
      setBusyLang(null);
    }
  };

  const schedule = async () => {
    if (!date) return toast.error("Pick a date first");
    setScheduling(true);
    try {
      await api.post("/campaigns/schedule", { name: c.name, img: c.img, caption: translated?.text || c.caption, date });
      toast.success(`Reminder scheduled for ${date} — you'll get the creative + caption by email that morning`);
      setDate("");
      window.dispatchEvent(new Event("flier-scheduled"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not schedule");
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div className="mt-3 border-t border-[#27272A] pt-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Languages className="h-3.5 w-3.5 text-neutral-500" />
        {LANGS.map((l) => (
          <button
            key={l.code}
            data-testid={`translate-${l.code}-${idx}`}
            onClick={() => translate(l.code)}
            disabled={busyLang !== null}
            className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
              translated?.lang === l.code ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#27272A] text-neutral-400 hover:text-white"
            }`}
          >
            {busyLang === l.code ? "…" : l.label}
          </button>
        ))}
        {translated && (
          <button
            data-testid={`copy-translated-${idx}`}
            onClick={() => { navigator.clipboard.writeText(translated.text); toast.success("Translated caption copied"); }}
            className="text-[10px] text-neutral-400 hover:text-white inline-flex items-center gap-1 ml-1"
          >
            <Copy className="h-3 w-3" /> copy
          </button>
        )}
      </div>
      {translated && <p className="text-xs text-neutral-300 mt-2 line-clamp-3" data-testid={`translated-text-${idx}`}>{translated.text}</p>}
      <div className="mt-2.5 flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
        <input
          type="date"
          data-testid={`schedule-date-${idx}`}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-[#121212] border border-[#27272A] rounded-md text-[11px] text-neutral-300 px-2 py-1 flex-1 min-w-0"
        />
        <button
          data-testid={`schedule-btn-${idx}`}
          onClick={schedule}
          disabled={scheduling}
          className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/25 transition-colors disabled:opacity-50"
        >
          {scheduling ? "…" : "Schedule"}
        </button>
      </div>
      {campaigns?.length > 0 && (
        <div className="mt-2.5 flex items-center gap-2">
          <QrCodeIcon className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
          <select
            data-testid={`qr-campaign-select-${idx}`}
            value={qrCode}
            onChange={(e) => setQrCode(e.target.value)}
            className="bg-[#121212] border border-[#27272A] rounded-md text-[11px] text-neutral-300 px-2 py-1.5 flex-1 min-w-0"
          >
            <option value="">QR link…</option>
            {campaigns.map((cp) => <option key={cp.code} value={cp.code}>{cp.name}</option>)}
          </select>
          <button
            data-testid={`qr-poster-btn-${idx}`}
            onClick={downloadQrPoster}
            disabled={qrBusy}
            className="text-[10px] font-bold px-3 py-1.5 rounded-full border border-[#27272A] text-neutral-300 hover:text-white hover:border-neutral-500 transition-colors disabled:opacity-50"
          >
            {qrBusy ? "…" : "QR poster"}
          </button>
        </div>
      )}
    </div>
  );
};

const ScheduledList = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/campaigns/schedule").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("flier-scheduled", load);
    return () => window.removeEventListener("flier-scheduled", load);
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="mt-5 rounded-lg border border-[#27272A] bg-black/30 p-4" data-testid="scheduled-fliers">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5" /> Scheduled posts (emailed each morning)
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((s) => (
          <li key={s.id} className="flex items-center gap-3 text-xs" data-testid={`scheduled-${s.id}`}>
            <img src={s.img} alt="" className="h-8 w-8 rounded object-cover border border-[#27272A]" />
            <span className="text-neutral-300 flex-1 truncate">{s.name}</span>
            <span className="text-neutral-500 font-mono">{s.date}</span>
            <button
              data-testid={`unschedule-${s.id}`}
              onClick={async () => { await api.delete(`/campaigns/schedule/${s.id}`).catch(() => {}); load(); }}
              className="text-neutral-500 hover:text-[#FF3B30]"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CREATIVES = [
  {
    img: "/campaign/speed-flier.jpg",
    name: "0.2 Seconds — Speed to Buyer",
    format: "Portrait — WhatsApp status, IG stories, TikTok, print",
    caption: "⚡ 0.2 SECONDS. That's how fast your product can meet its buyer on VISMARKS — live to all buyers in under a second, matched to waiting buyers instantly, found by AI search even with different words. On major marketplaces, new products can wait days. List it now → vismarks.com",
  },
  {
    img: "/campaign/about-poster.jpg",
    name: "All About VISMARKS — Master Poster",
    format: "Portrait — print, WhatsApp status, IG stories, event posters",
    caption: "⚡ Sell globally at light-speed on VISMARKS. Keep up to 99.8% of every sale — one flat fee, zero commissions, unlimited products. AI lists your product by voice in 60 seconds in 36 languages, with buyer protection on every order. Founding sellers: first month FREE → vismarks.com",
  },
  {
    img: "/campaign/about-square.jpg",
    name: "All About VISMARKS — Square Social",
    format: "Square — Instagram/Facebook feed, LinkedIn",
    caption: "Flat fees. Verified sellers. AI on your side. 🌍 Keep $99.80 of every $100, sell in 36 languages, list by voice in 60 seconds, protected until delivery. That's VISMARKS → vismarks.com",
  },
  {
    img: "/campaign/about-banner.jpg",
    name: "All About VISMARKS — Wide Banner",
    format: "Landscape — website header, YouTube/Twitter banner, press",
    caption: "Everything a marketplace should be: keep $99.80 of every $100 · protected until delivery · AI lists it in 60s · 36 languages · listings never expire. Founding sellers: first month FREE → vismarks.com",
  },
  {
    img: "/campaign/compare-all.jpg",
    name: "VS All Marketplaces — Master Comparison",
    format: "Landscape — website banners, LinkedIn, press, investor decks",
    caption: "Why sellers & buyers choose VISMARKS over Amazon, Jumia, eBay, AliExpress & Etsy: flat fees in cents, money protected until delivery, AI voice listing, Demand Radar, 36 languages, listings that never expire. See the full comparison → vismarks.com",
  },
  {
    img: "/campaign/compare-fees.jpg",
    name: "VS Fees — Keep $99.80",
    format: "Story/Portrait — WhatsApp status, IG stories, TikTok",
    caption: "You sell $100. Other marketplaces keep up to $15. On VISMARKS you keep $99.80. ✅ Flat cents, not percentages. Keep what you earn → vismarks.com",
  },
  {
    img: "/campaign/compare-protection.jpg",
    name: "VS Trust — Buyer Protection",
    format: "Story/Portrait — buyer-focused channels",
    caption: "Most marketplaces: pay and hope. VISMARKS: pay, protected, delivered. 🛡 Every seller ID-verified · automatic refunds if not shipped in 48h · dispute chat. Your money is protected until delivery → vismarks.com/buyer-protection",
  },
  {
    img: "/campaign/compare-ai.jpg",
    name: "VS AI Tools — They Don't Have This",
    format: "Story/Portrait — seller recruitment",
    caption: "The only marketplace with AI on your side: 🎙 speak your listing live in 60s · 📸 AI studio photos · 📡 Demand Radar finds buyers already waiting · 🌍 sell in 36 languages automatically. Amazon, Jumia, eBay: none of these → vismarks.com",
  },
  {
    img: "/campaign/compare-languages.jpg",
    name: "VS Languages — One Shop, 31 Languages",
    format: "Story/Portrait — global & diaspora audiences",
    caption: "Most marketplaces speak one language. VISMARKS speaks 31. 🌍 Your listings auto-translate for buyers from Lagos to Tokyo. One shop, the whole world → vismarks.com",
  },
  {
    img: "/campaign/compare-freedom.jpg",
    name: "VS Listing Fees — List Free, List Forever",
    format: "Story/Portrait — seller recruitment",
    caption: "Etsy charges per listing. Amazon takes monthly cuts. VISMARKS doesn't. ♾ Listings never expire · bulk upload 500 products · no commission eating your profit. Founding sellers: first month FREE → vismarks.com",
  },
  {
    img: "/campaign/founders-fr.jpg",
    name: "Founders 🇫🇷 Français",
    format: "Square — francophone markets (Côte d'Ivoire, Sénégal, Cameroun, RDC)",
    caption: "⚡ PREMIER MOIS GRATUIT + ANNONCES ILLIMITÉES — seulement pour les 20 premiers vendeurs fondateurs sur VISMARKS. Les places partent vite. Réclamez la vôtre → vismarks.com #vendeurfondateur",
  },
  {
    img: "/campaign/founders-sw.jpg",
    name: "Founders 🇰🇪 Kiswahili",
    format: "Square — East Africa (Kenya, Tanzania, Uganda)",
    caption: "⚡ MWEZI WA KWANZA BURE + ORODHESHA BILA KIKOMO — kwa wauzaji 20 wa kwanza tu kwenye VISMARKS. Nafasi zinakwisha. Chukua yako sasa → vismarks.com #muuzajimwanzilishi",
  },
  {
    img: "/campaign/fees-fr.jpg",
    name: "15% Problem 🇫🇷 Français",
    format: "Square — francophone markets",
    caption: "Vous vendez 100$. Ils gardent 15$. ❌ Sur VISMARKS vous gardez 99,80$. ✅ Des centimes fixes, pas de pourcentages. → vismarks.com",
  },
  {
    img: "/campaign/fees-sw.jpg",
    name: "15% Problem 🇰🇪 Kiswahili",
    format: "Square — East Africa",
    caption: "Umeuza $100. Wanachukua $15. ❌ Kwenye VISMARKS unabaki na $99.80. ✅ Senti chache tu, siyo asilimia. → vismarks.com",
  },
  {
    img: "/campaign/matching-fr.jpg",
    name: "AI Matching 🇫🇷 Français",
    format: "Square — francophone markets",
    caption: "Votre produit trouve son acheteur en quelques SECONDES. Dès que vous publiez, l'IA de VISMARKS alerte les acheteurs qui le cherchent déjà. ⚡ vismarks.com",
  },
  {
    img: "/campaign/matching-sw.jpg",
    name: "AI Matching 🇰🇪 Kiswahili",
    format: "Square — East Africa",
    caption: "Bidhaa yako inapata mnunuzi kwa SEKUNDE. Ukiweka bidhaa tu, AI ya VISMARKS inawajulisha wanunuzi wanaoitafuta. ⚡ vismarks.com",
  },
  {
    img: "/campaign/60s-fr.jpg",
    name: "60-Second Shop 🇫🇷 Français",
    format: "Square — francophone markets",
    caption: "60 secondes. Une note vocale. Une boutique. 🎤 « Paniers tissés à la main, 25$, 10 en stock » — l'IA fait le reste, photo comprise. vismarks.com",
  },
  {
    img: "/campaign/60s-sw.jpg",
    name: "60-Second Shop 🇰🇪 Kiswahili",
    format: "Square — East Africa",
    caption: "Sekunde 60. Sauti moja. Duka moja. 🎤 'Vikapu vya mikono, $25, 10 stoki' — AI inafanya mengine yote, pamoja na picha. vismarks.com",
  },
  {
    img: "/campaign/60-second-shop-story.jpg",
    name: "60-Second Shop (story)",
    format: "Story / Reel cover — Instagram, TikTok, WhatsApp Status",
    caption: "I built my online shop in 60 SECONDS. 🎤 Spoke my product → AI wrote the listing → AI shot the photo → published. First month FREE for founders → vismarks.com #60secondshop #AItools",
  },
  {
    img: "/campaign/60-second-shop-square.jpg",
    name: "60-Second Shop (square)",
    format: "Square — Instagram / Facebook feed",
    caption: "60 seconds. One voice note. One shop. 🎤 'Handwoven baskets, $25, 10 in stock' — AI does the rest, photo included. vismarks.com #voicelisting #sellonline",
  },
  {
    img: "/campaign/15-percent-problem.jpg",
    name: "The 15% Problem (square)",
    format: "Square — Instagram / Facebook / X",
    caption: "You sold $100. They keep $15. ❌ On VISMARKS you keep $99.80. ✅ Flat cents, not percentages. vismarks.com #the15percentproblem #ecommerce",
  },
  {
    img: "/campaign/15-percent-story.jpg",
    name: "The 15% Problem (story)",
    format: "Story / Reel — Instagram, WhatsApp Status, TikTok",
    caption: "Do the math before you list anywhere. 💸 $100 sale = $15 gone on big marketplaces. On VISMARKS you keep $99.80. Flat cents. Not percentages. → vismarks.com",
  },
  {
    img: "/campaign/buyer-waiting.jpg",
    name: "Buyer Was Waiting (landscape)",
    format: "Landscape — X / Telegram / LinkedIn / Facebook",
    caption: "Your buyer was already waiting. ⚡ Demand Radar showed 3 buyers wanting phone accessories — one upload later, they all got the alert. VISMARKS matches in seconds → vismarks.com",
  },
  {
    img: "/campaign/buyer-waiting-story.jpg",
    name: "Buyer Was Waiting (story)",
    format: "Story / Reel — Instagram, WhatsApp Status, TikTok",
    caption: "MATCH FOUND ⚡ Imagine uploading a product and the buyer is alerted before you close the app. That's VISMARKS. Your buyer was already waiting → vismarks.com",
  },
  {
    img: "/campaign/founders-unlimited.jpg",
    name: "Founders unlimited",
    format: "Square — Instagram / Facebook / WhatsApp",
    caption: "⚡ FIRST MONTH FREE + UNLIMITED LISTINGS — only for the first 20 founding sellers on VISMARKS. Spots are counting down live. Claim yours → vismarks.com #foundingseller #sellonline",
  },
  {
    img: "/campaign/ai-matching-flier.jpg",
    name: "AI matching speed",
    format: "Square — Instagram / Facebook feed",
    caption: "Your product meets its buyer in SECONDS. The moment you list, VISMARKS' AI alerts every buyer already asking for it. ⚡ vismarks.com #AImatching #ecommerce",
  },
  {
    img: "/campaign/low-fees.jpg",
    name: "Fees comparison",
    format: "Square — Instagram / Facebook / X",
    caption: "Marketplaces take up to 15% of every sale. VISMARKS takes cents — flat, per unit. No percentages, no surprises. Your profit stays yours. ⚡ vismarks.com #sellonline",
  },
  {
    img: "/campaign/ai-tools.jpg",
    name: "AI seller toolkit",
    format: "Story / Reel — Instagram, WhatsApp Status, TikTok",
    caption: "Speak it — AI builds your shop. 🎤 Voice listing, AI product photos, AI descriptions, 500-product bulk upload. Your whole catalogue live in minutes. ⚡ vismarks.com #AItools",
  },
  {
    img: "/campaign/voice-audio.jpg",
    name: "Voice shopping",
    format: "Square — Instagram / Facebook / WhatsApp",
    caption: "Shop with your voice 🎙️ — tap the mic and say what you want. Tap Listen and any product reads itself aloud. Shopping made effortless on VISMARKS ⚡ vismarks.com #voicesearch",
  },
  {
    img: "/campaign/google-reach-flier.jpg",
    name: "Google reach",
    format: "Landscape — X / Telegram / LinkedIn",
    caption: "List once on VISMARKS → appear on Google automatically. Free Google Shopping distribution built into every listing. ⚡ vismarks.com #googleshopping",
  },
  {
    img: "/campaign/demand-radar-flier.jpg",
    name: "Demand Radar",
    format: "Landscape — X / Telegram / LinkedIn",
    caption: "Know what buyers want BEFORE you upload. Demand Radar shows live buyer requests — fill one and the buyer is alerted in seconds. Only on VISMARKS ⚡ vismarks.com",
  },
  {
    img: "/campaign/security-signin.jpg",
    name: "Security & trust",
    format: "Square — Instagram / Facebook / LinkedIn",
    caption: "Bank-grade security, one-tap Google sign-in, login alerts, trusted devices — and payouts go straight to the seller. Trade with confidence ⚡ vismarks.com #securecommerce",
  },
  {
    img: "/campaign/commission.jpg",
    name: "Commission killer",
    format: "Square — Instagram / Facebook feed",
    caption: "Marketplaces take up to 15% of every sale you make. VISMARKS takes cents. Flat fee. No percentage. Your profit stays yours. ⚡ Start selling → vismarks.com #ecommerce #sellonline #marketplace",
  },
  {
    img: "/campaign/bulk-upload.jpg",
    name: "Bulk upload speed",
    format: "Square — Instagram / Facebook feed",
    caption: "Upload 500 products before lunch. 🍽️ Drop in your spreadsheet — our AI writes every title, description and even generates the photos. Your whole catalogue, live in minutes. ⚡ vismarks.com #bulkupload #AItools #onlinebusiness",
  },
  {
    img: "/campaign/founders.jpg",
    name: "Founders urgency",
    format: "Story / Reel — Instagram, WhatsApp Status, TikTok",
    caption: "🔥 FOUNDING SELLER OFFER: the first 20 sellers get their FIRST MONTH FREE + a permanent Founding Seller badge. Spots are counting down live on the site. Claim yours → vismarks.com #foundingmember #sellonline",
  },
  {
    img: "/campaign/instant-match.jpg",
    name: "Instant matching",
    format: "Landscape — X / Telegram / LinkedIn",
    caption: "Your product meets its buyer in SECONDS, not weeks. VISMARKS' AI matching engine connects every upload to the buyers already asking for it. ⚡ vismarks.com #AImatching #ecommerce",
  },
  {
    img: "/campaign/google-reach.jpg",
    name: "Google reach (alt)",
    format: "Story / Reel — Instagram, WhatsApp Status, TikTok",
    caption: "List once on VISMARKS → appear on Google automatically. Free Google Shopping distribution is built into every listing. Your products, found by the whole internet. ⚡ vismarks.com #googleshopping #sellonline",
  },
  {
    img: "/campaign/demand-radar.jpg",
    name: "Demand Radar (alt)",
    format: "Landscape — X / Telegram / LinkedIn",
    caption: "What if you knew what buyers wanted BEFORE you uploaded? Demand Radar shows sellers live buyer requests — upload a match and the buyer is alerted in seconds. Only on VISMARKS ⚡ vismarks.com",
  },
];

export const CampaignKitCard = () => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/campaigns/creatives").then(({ data }) => setGenerated(data)).catch(() => {});
    api.get("/campaigns").then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  }, []);

  const generate = async () => {
    if (prompt.trim().length < 5) return toast.error("Describe the creative in a few words first");
    setGenBusy(true);
    try {
      const { data } = await api.post("/campaigns/creative", { prompt: prompt.trim() }, { timeout: 120000 });
      setGenerated((g) => [{ prompt: prompt.trim(), image_url: data.image_url }, ...g].slice(0, 12));
      setPrompt("");
      toast.success("Creative generated — download and post it");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed — try again");
    } finally {
      setGenBusy(false);
    }
  };

  const copyCaption = (c) => {
    navigator.clipboard.writeText(c);
    toast.success("Caption copied — paste it with the image");
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="campaign-kit-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[#FF3B30]" />
          <h2 className="font-bold text-lg">Campaign kit</h2>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 border border-[#27272A] rounded-full px-2 py-0.5">38 ready-to-post creatives · EN/FR/SW · incl. Speed, All-About + 6 comparisons</span>
          <a href="/campaign/vismarks-comparison.pdf" download="vismarks-comparison.pdf" data-testid="comparison-pdf-link"
            className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded-full px-2.5 py-0.5 hover:bg-emerald-500/20">
            ⬇ Comparison PDF (1-pager)
          </a>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="campaign-kit-toggle"
          onClick={() => setOpen(!open)}
          className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white"
        >
          {open ? "Hide" : "Open kit"}
        </Button>
      </div>
      {open && (
        <>
        <SalesPitchPanel />
        <div className="mt-5 rounded-lg border border-[#007AFF]/30 bg-[#007AFF]/5 p-4" data-testid="video-ads-section">
          <div className="text-xs uppercase tracking-widest text-[#007AFF] font-bold">
            🎬 Narrated video ads — TikTok / Reels / WhatsApp status
          </div>
          <p className="mt-1 text-xs text-neutral-400">
            30–39s ads with the official pitch voiceover. Vertical (9:16) for TikTok/Reels/Status, square (1:1) for feeds.
          </p>
          <div className="mt-3 grid sm:grid-cols-3 gap-2">
            {[
              { l: "English", c: "en" },
              { l: "Français", c: "fr" },
              { l: "Kiswahili", c: "sw" },
            ].map((v) => (
              <div key={v.c} className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-3">
                <div className="text-sm font-bold text-white">{v.l}</div>
                <div className="mt-2 flex gap-2">
                  <a href={`/campaign/video/vismarks-ad-${v.c}-vertical.mp4`} download data-testid={`video-ad-${v.c}-vertical`}
                    className="text-xs font-semibold text-[#007AFF] border border-[#007AFF]/40 rounded-full px-3 py-1 hover:bg-[#007AFF]/10">
                    ⬇ Vertical 9:16
                  </a>
                  <a href={`/campaign/video/vismarks-ad-${v.c}-square.mp4`} download data-testid={`video-ad-${v.c}-square`}
                    className="text-xs font-semibold text-neutral-300 border border-[#27272A] rounded-full px-3 py-1 hover:bg-white/5">
                    ⬇ Square 1:1
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
        <AiVideoAdsPanel />
        <ScheduledList />
        <div className="mt-5 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-4" data-testid="creative-generator">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF3B30] font-bold">
            <Sparkles className="h-3.5 w-3.5" /> Generate a new creative with AI
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="creative-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              placeholder="e.g. Market trader uploading products from her phone, founders countdown urgency"
              className="bg-[#121212] border-[#27272A] flex-1"
            />
            <Button
              data-testid="creative-generate-btn"
              onClick={generate}
              disabled={genBusy}
              className="rounded-full bg-[#FF3B30] text-white hover:bg-[#e0342a] font-bold shrink-0"
            >
              {genBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {genBusy ? "Generating…" : "Generate"}
            </Button>
          </div>
          {generated.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="generated-creatives">
              {generated.map((g, i) => (
                <div key={i} className="rounded-lg border border-[#27272A] overflow-hidden bg-black/30">
                  <img src={g.image_url} alt={g.prompt} className="w-full aspect-square object-cover" loading="lazy" />
                  <div className="p-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-neutral-500 truncate">{g.prompt}</span>
                    <a href={g.image_url} download data-testid={`download-generated-${i}`} className="text-neutral-400 hover:text-white shrink-0">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CREATIVES.map((c, i) => (
            <div key={i} className="rounded-lg border border-[#27272A] bg-black/30 overflow-hidden" data-testid={`creative-${i}`}>
              <img src={c.img} alt={c.name} className="w-full aspect-square object-cover" loading="lazy" />
              <div className="p-4">
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{c.format}</div>
                <p className="text-xs text-neutral-400 mt-2 line-clamp-3">{c.caption}</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" data-testid={`copy-caption-${i}`} onClick={() => copyCaption(c.caption)}
                    className="rounded-full bg-white text-black hover:bg-neutral-200 h-8 px-4 text-xs font-semibold">
                    <Copy className="h-3 w-3 mr-1" /> Copy caption
                  </Button>
                  <a href={c.img} download data-testid={`download-creative-${i}`}>
                    <Button size="sm" variant="outline" className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-8 px-4 text-xs">
                      <Download className="h-3 w-3 mr-1" /> Image
                    </Button>
                  </a>
                </div>
                <CreativeExtras c={c} idx={i} campaigns={campaigns} />
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
};
